import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IStore } from '../../../../../../store.type';
import { change, Field, getFormValues, reset } from 'redux-form';
import { Box, BannerContainer, Wrapper } from './styles';
import { required } from '@Utils/validation';
import {
  CHEVRON_RIGHT_ICON,
  CLOSE_NEW_ICON,
  SMALL_FLASH_ICON,
  INFO_NEW_ICON,
  PIGGY_BANK_ICON
} from '@Images';
import { CommonDropdownOutlined, ModalFactory } from '@Components';
import {
  PaymentMethodProps,
  PayoutMethodDropdownProps,
  ReceivingMethodFormValueType
} from '../../utils/types/types';
import {
  getFastestPaymentMethod,
  getNewPaymentMethodsForCountryCurrency
} from '../../utils/methods';
import { payoutMethodInfo, valueToRoutingTypeMap } from '../../utils/constants';
import { RECEIVING_METHOD_FORM_NAME } from '../../../../constants';
import { getPayeeJSONSchemaByRoutingConfigId } from '@Redux/actions/payee-data';
import {
  setSchemaApiValues,
  setSelectedLabelAction
} from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import { selectNewBeneState } from '@Redux/selectors';
import { BENE_BANK_DETAILS_FORM_NAME } from '../../../../constants/commons';

export const PayoutMethodDropdown: React.FC<PayoutMethodDropdownProps> = (
  props
): React.ReactElement => {
  const { paymentMethods, beneState, isEditFlow } = props;
  const formValues = useSelector((state: IStore) =>
    getFormValues(RECEIVING_METHOD_FORM_NAME)(state)
  ) as ReceivingMethodFormValueType;

  const isBeneSchemaEnabled = useSelector(selectNewBeneState)?.isBeneSchemaEnabled;
  const [nonBeneDropdown, setNonBeneDropdown] = useState<PaymentMethodProps[]>([]);

  const dispatch = useDispatch<AppDispatch>();

  const formatForNonBeneSchema = () => {
    const { beneCashPayoutDetails, debit_card_details, beneWalletDetails, additionalFields } =
      beneState;
    const dropDown = [];
    if (beneCashPayoutDetails)
      dropDown.push({
        label: 'Cash pickup',
        subtitle: `${
          beneCashPayoutDetails.routing_config_type === 'CASH_PAYOUT'
            ? `Select cash pickup partner`
            : `Collect cash from available cash pickup partner`
        }`,
        value: `${
          beneCashPayoutDetails.routing_config_type === 'CASH_PAYOUT' ? `cash` : `money_cash`
        }`,
        key: 'cash'
      });
    if (debit_card_details)
      dropDown.push({
        label: 'Debit card',
        value: 'card',
        key: 'card',
        subtitle: 'Please provide recipient’s VISA debit card details '
      });
    if (beneWalletDetails)
      dropDown.push({
        label: 'Wallet',
        value: 'wallet',
        key: 'wallet',
        subtitle: "Please provide recipient's wallet details"
      });
    if (additionalFields)
      dropDown.push({
        label: 'Bank deposit',
        value: 'bank',
        key: 'bank',
        subtitle: 'Please provide recipient’s bank account details'
      });
    setNonBeneDropdown(dropDown);
  };

  const formattedPaymentMethods = paymentMethods?.map((item: any) => {
    return {
      image: payoutMethodInfo[item.routing_config_type]?.image ?? PIGGY_BANK_ICON,
      label: item.label,
      description: item.note,
      type: item.routing_config_type,
      payout_method: item.payout_method,
      transfer_limit: item?.meta?.notes?.inward
    };
  });

  const formattedPaymentMethodForNonBene = nonBeneDropdown?.map((item: PaymentMethodProps) => {
    const routingType = valueToRoutingTypeMap[item.value];

    const correspondingPaymentMethod: any = paymentMethods?.find((pm: any) => {
      if (Array.isArray(routingType)) {
        return routingType.includes(pm.routing_config_type);
      }
      return pm.routing_config_type === routingType;
    });

    return {
      image:
        payoutMethodInfo[correspondingPaymentMethod?.routing_config_type]?.image ?? PIGGY_BANK_ICON,
      label: item.label,
      description: item.subtitle,
      type: correspondingPaymentMethod?.routing_config_type,
      payout_method: item.value,
      transfer_limit: correspondingPaymentMethod?.notes?.inward
    };
  });

  const prevCountryRef = useRef(formValues?.bank_account_country);
  // Reset payout_method whenever payment options change or when the bank account country changes.
  useEffect(() => {
    const prevCountry = prevCountryRef.current;
    const currentCountry = formValues?.bank_account_country;
    if (!paymentMethods?.length || prevCountry !== currentCountry) {
      dispatch(change(RECEIVING_METHOD_FORM_NAME, 'payout_method', null));
    }
    prevCountryRef.current = currentCountry;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentMethods,
    formValues?.currency_code,
    formValues?.bank_account_country,
    formValues?.account_type
  ]);
  useEffect(() => {
    if (!isBeneSchemaEnabled) formatForNonBeneSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBeneSchemaEnabled, beneState]);

  const staticValues = isBeneSchemaEnabled
    ? (formattedPaymentMethods ?? []).map((m, index) => ({
        key: index,
        value: m.payout_method,
        text: m.label
      }))
    : (formattedPaymentMethodForNonBene ?? []).map((m, index) => ({
        key: index,
        value: m.payout_method,
        text: m.label
      }));

  const modalMethods = isBeneSchemaEnabled
    ? formattedPaymentMethods
    : formattedPaymentMethodForNonBene;

  const onPaymentMethodSelect = async (method: PaymentMethodProps) => {
    if (method?.payout_method !== formValues?.payout_method) {
      dispatch(reset(BENE_BANK_DETAILS_FORM_NAME));
    }
    dispatch(change(RECEIVING_METHOD_FORM_NAME, 'payout_method', method?.payout_method));
    dispatch(setSelectedLabelAction(method.label));
    if (isBeneSchemaEnabled) {
      const schemaResponse = await dispatch(
        getPayeeJSONSchemaByRoutingConfigId(formValues, method?.payout_method, method?.type)
      );
      dispatch(setSchemaApiValues(schemaResponse));
    }
  };

  const showModal = async () => {
    const fastestMethod = getFastestPaymentMethod(paymentMethods || []);
    const fastestRoutingType = fastestMethod?.routing_config_type;
    const newPaymentMethod = getNewPaymentMethodsForCountryCurrency(
      formValues.bank_account_country,
      formValues.currency_code
    );
    //@ts-ignore
    await ModalFactory.builder(
      (onClose: any) => (
        <Wrapper>
          <div className="popular-countries-container__close-icon-container">
            <img
              src={CLOSE_NEW_ICON}
              alt="close"
              className="close-icon-container__close-icon"
              onClick={() => onClose(true)}
            />
          </div>
          <span className="container__heading">{'Select receiving method'}</span>

          {modalMethods?.map((method: any, index: number) => (
            <>
              {newPaymentMethod?.includes(method.type) && (
                <BannerContainer>
                  <div className="new-payment-method-banner">{'New'}</div>
                  <div className="triangle"></div>
                </BannerContainer>
              )}

              <Box
                key={index}
                onClick={() => {
                  onPaymentMethodSelect(method);
                  onClose(true);
                }}
              >
                <div className="inner-wrapper">
                  <div className="text-wrapper">
                    <div className="image-container">
                      <img
                        src={method.image}
                        alt={method.label}
                        className="close-icon-container__close-icon"
                      />
                    </div>
                    <div className="mid-section-container">
                      <span className="main-text">
                        {method.label}
                        {method.type === fastestRoutingType ? (
                          <span className="faster-tag-box">
                            <img src={SMALL_FLASH_ICON} alt="chevron-right" width={10} />
                            <span className="faster-tag">{'Faster'}</span>
                          </span>
                        ) : null}
                      </span>
                      <span className="sub-text">{method.description}</span>
                    </div>
                  </div>
                  <div className="verification-method-right-icon">
                    <img src={CHEVRON_RIGHT_ICON} alt="chevron-right" />
                  </div>
                </div>
                <div className="grey-box">
                  <img src={INFO_NEW_ICON} alt="chevron-right" width={10} />
                  <span className="transfer-limit-text">{`Transfer limit: `} </span>
                  <span className="value"> {`${method.transfer_limit}`}</span>
                </div>
              </Box>
            </>
          ))}
        </Wrapper>
      ),
      'receiving-method-modal insta-modal center-modal',
      true
    );
  };

  return (
    <div className="innerBox">
      <Field
        bindToThisObject={formValues?.payout_method}
        component={CommonDropdownOutlined}
        width="100%"
        name="payout_method"
        type="text"
        placeholder={'Receiving method'}
        className="theme-dropdown"
        staticValues={staticValues}
        value={formValues?.payout_method}
        validate={[required]}
        onClick={showModal}
        disabled={isEditFlow}
      ></Field>
    </div>
  );
};
